import { queryKeys } from '@query/keys'
import { useQuery } from '@tanstack/react-query'
import { FormikErrors, FormikProps, useFormikContext, withFormik } from 'formik'
import { t } from 'i18next'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Form } from '@lib/formik'

import { DocumentChooseAnotherModal } from '@pages/Signup/Pages/DocumentsInfo/DocumentInfoPageStep1/modals/DocumentChooseAnother.modal'
import { DocumentReasonModal } from '@pages/Signup/Pages/DocumentsInfo/DocumentInfoPageStep1/modals/DocumentReason.modal'

import { useSessionEntity } from '@context/EntityContext'
import { useModal } from '@context/ModalsContext'

import { Body1 } from '@ui/Typography'
import { Button } from '@ui/components/Button'
import { FileData } from '@ui/components/FileUpload/types'

import { useApiClient } from '@utils/ApiClient'
import {
  useCategorySizesReadContext,
  useCategorySizesWriteContext,
} from '@utils/CategorySizesContext'
import { ClientApiClient } from '@utils/clientApi'
import { TickmillCompaniesEnum } from '@utils/companyName.utils'
import { isDoubleSidedDocument } from '@utils/uploadDocument'
import { removeEmojis } from '@utils/validations'

import { ClientNciPriorityDto } from '@model/ClientNciPriorityDto'
import { ClientNciHistoryStatus } from '@model/CreateNciHistory'
import { DocumentCategoryType } from '@model/DocumentCategories'
import { NameDto, ShortNameDto } from '@model/NameDto'

import { FormTemplate } from '@global/templates/FormTemplate'
import { TransactionFormActionButtonTemplate } from '@global/templates/TransactionFormActionButtonTemplate'

import { useOnLocaleSwitch } from '@hooks/useOnLocaleSwitch'
import { scrollToTop } from '@hooks/useScrollToTop'

import { ActionModal } from '@shared/modals/Action.modal'

import { DocumentFileUploadsFactory } from '../../DocumentFileUploadsFactory'
import { DocumentNumberFieldFactory } from './DocumentNumberFieldFactory'

export interface DocumentManagementNCIFormValues {
  nciHistory: DocumentManagementNciHistory
  documents: DocumentManagementDocument[]
  reason?: string
  accountNciPriorities: ClientNciPriorityDto[]
  accountNciAvailablePriorities: ClientNciPriority
}

interface DocumentManagementNciHistory {
  statusId: number
  nciPriorityId: number
  documentTypeId: number
  nciDocumentNumber: string
  countryId: number
}

interface DocumentManagementDocument {
  categoryId: number
  typeId: number
  file: string
  filename: string
  error: boolean
}

interface ClientNciPriority extends Omit<ClientNciPriorityDto, 'nciRule' | 'documentTypes'> {
  nciRule: ShortNameDto<string> & {
    nciType: string
  }
  documentTypes: {
    name: string
    documentTypeId: number
  }[]
}

export const DocumentManagementNCIFormUI: React.FC<
  FormikProps<DocumentManagementNCIFormValues> & OuterProps
> = (props) => {
  const { values, onCancel, documentCategory, handleChooseAnotherDoc } = props
  useOnLocaleSwitch(() => props.validateForm())

  const { t } = useTranslation()

  const handlePageExitConfirmation = () => {
    onCancel()
  }

  const exitModal = useModal(ActionModal, {
    title: t('Cancel Adding Document'),
    description: t('Do you really like to cancel document adding process'),
    onConfirm: handlePageExitConfirmation,
    onClose: () => scrollToTop(),
  })

  return (
    <FormTemplate title={t('Sign up.Add Document')} goBack={exitModal.open}>
      <Form>
        <FormFields
          documentTypeId={values.nciHistory.documentTypeId}
          documentCategory={documentCategory.id}
          onCancel={() => exitModal.open()}
          handleChooseAnotherDoc={handleChooseAnotherDoc}
        />
      </Form>
    </FormTemplate>
  )
}

interface FormFieldsFactoryProps {
  documentTypeId: number
  documentCategory: number
  handleChooseAnotherDoc(doc: ClientNciPriorityDto): void
  onCancel(): void
}

const FormFields: React.FC<FormFieldsFactoryProps> = (props) => {
  const { t } = useTranslation()
  const { documentTypeId, documentCategory, onCancel, handleChooseAnotherDoc } = props

  const apiClient = useApiClient(ClientApiClient)
  const { values, isValid, setValues, setFieldValue } =
    useFormikContext<DocumentManagementNCIFormValues>()
  const chooseAnotherModal = useModal(DocumentChooseAnotherModal)

  const reasonModal = useModal(DocumentReasonModal)
  const [needFileUploadRefresh, setNeedFileUploadRefresh] = useState(false)
  const [showChooseAnotherDoc, setShowChooseAnotherDoc] = useState(false)
  const [documentOptions, setDocumentOptions] = useState<ClientNciPriorityDto[]>([])
  const [goBackDocumentOptions, setGoBackDocumentOptions] = useState<ClientNciPriorityDto[]>([])
  const { categorySizes } = useCategorySizesReadContext()
  const { refreshCategorySizes } = useCategorySizesWriteContext()

  useEffect(() => {
    refreshCategorySizes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { data: documents } = useQuery({
    queryKey: queryKeys.profile.nciPriorities(),
    queryFn: () => apiClient.getAccountNciPriorities(),
  })

  const handleChooseAnotherDocument = () => {
    const isFinal = values.accountNciAvailablePriorities.final
    if (isFinal && documentOptions.length) {
      if (documentOptions.length > 2) {
        chooseAnotherModal.open({
          documentOptions: goBackDocumentOptions,
          onConfirm: (doc: ClientNciPriorityDto) => handleDocumentTypeChange(doc),
        })
      } else {
        handleChooseAnotherDoc(documentOptions[0])
      }
    } else {
      reasonModal.open({
        onSubmit: handleSubmitReason,
        documentType: values.accountNciAvailablePriorities.nciRule.shortName || '',
      })
    }
  }

  const handleSubmitReason = async (reason?: string) => {
    if (reason) {
      await apiClient.accountAcceptNciHistory({
        statusId: ClientNciHistoryStatus.INACTIVE,
        nciPriorityId: values.accountNciAvailablePriorities?.id || '',
        reason,
      })
      handleChooseAnotherDoc(
        documentOptions[
          documentOptions.findIndex((doc) => doc.id === values.accountNciAvailablePriorities.id) + 1
        ]
      )
    }
  }

  useEffect(() => {
    setFieldValue('nciHistory.nciDocumentNumber', removeEmojis(values.nciHistory.nciDocumentNumber))
  }, [setFieldValue, values.nciHistory.nciDocumentNumber])

  useEffect(() => {
    if (documents) {
      setGoBackDocumentOptions(documents.filter((doc) => !doc.final))
    }
    if (documents) {
      setDocumentOptions(documents)
    }
  }, [documents])

  useEffect(() => {
    setFieldValue('documents', [])
    setNeedFileUploadRefresh((prevState) => !prevState)
  }, [setFieldValue, values.nciHistory.documentTypeId])

  const handleDocumentUploads = (fileData: FileData[]) => {
    setValues({
      ...values,
      documents: fileData.map((x) => ({
        ...x,
        categoryId: DocumentCategoryType.Personal,
        typeId: values.nciHistory.documentTypeId,
        filename: x.fileName,
        file: x.base64Content,
        error: x.error ?? false,
        errorMessage: x.errorMessage ?? '',
      })),
    })
  }

  const handleDocumentTypeChange = (doc: ClientNciPriorityDto) => {
    handleChooseAnotherDoc(doc)
  }

  const isEUEntity = useSessionEntity() === TickmillCompaniesEnum.TICKMILL_EU

  useEffect(() => {
    if (goBackDocumentOptions.length) {
      setShowChooseAnotherDoc(true)
    } else {
      setShowChooseAnotherDoc(false)
    }
  }, [values.accountNciAvailablePriorities.final, goBackDocumentOptions])

  return (
    <React.Fragment>
      <div className='pb-2'>
        <Body1 className='pb-5'>
          {t('Sign up.Please be informed that your Primary Nationality is')}{' '}
          {values.accountNciAvailablePriorities.country?.name ?? t('N/A')}{' '}
          {t('Sign up.If you have any reservations, please contact', {
            entity: isEUEntity ? 'eu' : 'co.uk',
          })}
        </Body1>
        <Body1 isParagraph>
          {t('Sign up.In accordance with the new regulations of the Markets in', {
            entity: isEUEntity ? t('Europe') : t('UK'),
          })}
        </Body1>
      </div>
      <div className='pb-5'>
        <DocumentNumberFieldFactory
          showChooseAnotherDoc={showChooseAnotherDoc}
          handleChooseAnotherDocument={handleChooseAnotherDocument}
        />
      </div>
      <div className='pb-5'>
        <div className='pb-3'>
          <Body1 medium>{t('Sign up.Proof of ID-Document')}</Body1>
        </div>
        <div className='pb-4'>
          <Body1 isParagraph>
            {t('Sign up.Please upload an identification document displaying your full name')}
          </Body1>
        </div>
        <div>
          <div className='pb-3'>
            <DocumentFileUploadsFactory
              categorySizes={categorySizes}
              documentTypeId={documentTypeId}
              documentCategory={documentCategory}
              onChange={handleDocumentUploads}
              needFileUploadRefresh={needFileUploadRefresh}
            />
          </div>
        </div>
      </div>
      <TransactionFormActionButtonTemplate>
        <Button.Secondary size='lg' onClick={onCancel}>
          {t('Cancel')}
        </Button.Secondary>
        <Button.Primary size='lg' type='submit' disabled={!isValid}>
          {t('Confirm')}
        </Button.Primary>
      </TransactionFormActionButtonTemplate>
    </React.Fragment>
  )
}

interface OuterProps {
  documentCategory: NameDto
  documentCategoryType: NameDto
  accountNciPriorities: ClientNciPriorityDto[]
  accountNciAvailablePriorities: ClientNciPriorityDto
  onSubmit(values: DocumentManagementNCIFormValues): void
  onCancel(): void
  handleChooseAnotherDoc(doc: ClientNciPriorityDto): void
}

interface StateProps {
  documentCategory: NameDto
  documentCategoryType: NameDto
  accountNciPriorities: ClientNciPriorityDto[]
  accountNciAvailablePriorities: ClientNciPriorityDto
}

const initialState = (props: StateProps): DocumentManagementNCIFormValues => {
  const {
    documentCategory,
    documentCategoryType,
    accountNciPriorities,
    accountNciAvailablePriorities,
  } = props || {}
  const [documentType] = accountNciAvailablePriorities.documentTypes

  return {
    nciHistory: {
      statusId: 0,
      nciPriorityId: 0,
      documentTypeId: documentCategoryType?.id || documentType?.documentTypeId || 0,
      nciDocumentNumber: '',
      countryId: documentCategory?.id || 0,
    },
    documents: [],
    accountNciPriorities,
    accountNciAvailablePriorities: {
      ...accountNciAvailablePriorities,
      nciRule: {
        id: accountNciAvailablePriorities.nciRule.id,
        name: accountNciAvailablePriorities.nciRule.name || '',
        shortName: accountNciAvailablePriorities.nciRule.shortName || '',
        nciType: accountNciAvailablePriorities.nciRule.nciType || '',
      },
      documentTypes: accountNciAvailablePriorities.documentTypes.map((x) => ({
        name: x.name || '',
        documentTypeId: x.documentTypeId,
      })),
    },
  }
}

export const DocumentManagementNCIForm = withFormik<OuterProps, DocumentManagementNCIFormValues>({
  mapPropsToValues: (props) => {
    return initialState(props)
  },
  handleSubmit: (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)
      props.onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values, props) => {
    const errors: FormikErrors<DocumentManagementNCIFormValues> = {}

    if (!values.nciHistory.nciDocumentNumber) {
      errors.documents = t('Validation.Required')
    }
    const documents = values.documents.filter((x) => !!x?.filename).filter((x) => !x.error)

    const doubleSidedDocumentSize = isDoubleSidedDocument(
      props.documentCategory?.id,
      Number(values.nciHistory.documentTypeId)
    )
      ? 1
      : 0

    if (documents.length <= doubleSidedDocumentSize) {
      errors.documents = t('Validation.Required')
    }

    if (values.documents.some((doc) => doc.error)) {
      errors.documents = t('Validation.Required')
    }

    return errors
  },
  enableReinitialize: true,
  validateOnMount: true,
})(DocumentManagementNCIFormUI)
